/**
 * Pure helpers for the navigator outbox (#86a). Kept free of Angular and NgRx so the spec can
 * prove the rules directly.
 */

/** One `updateEventNavigator` call, as the crew's device built it. */
export interface NavigatorWrite {
  eventId: string;
  /** Left out by a write that records something about the run without moving the step. */
  currentStep?: string;
  navigatorData: string;
  /**
   * The ops revision this device had seen when it built the write (#86b). A write built before ops
   * moved the crew or reset the run loses to it on the server. Left out: no check.
   */
  basedOnOpsRevision?: number;
  /**
   * The crew's inventory changes from Confirm Scope, as JSON (`InventoryChanges`, #86c). The server
   * merges them item by item into the job's inventory.
   */
  inventoryChanges?: string;
}

/** One change of the crew's to an inventory item: what they started from, and what they set (#86c). */
export interface InventoryItemChange {
  id: string;
  roomId: string;
  /** Lets the server find a room stored without an id, from before room ids. */
  roomName: string;
  /** `null`: the crew added it. */
  before: Record<string, any> | null;
  /** `null`: the crew removed it. */
  after: Record<string, any> | null;
}

/** The crew's inventory changes at Confirm Scope, per item and per room (#86c). */
export interface InventoryChanges {
  // A room `before` is the stored room as it was, name first — a deletion keeps
  // the room's other fields too, so the server can apply `after: null` to it.
  rooms: { id: string; before: ({ name: string } & Record<string, any>) | null; after: Record<string, any> | null }[];
  items: InventoryItemChange[];
}

/** An item or room where the server kept the office's version over the crew's change (#86c). */
export interface KeptInventoryItem {
  id: string;
  name: string;
  reason: string;
}

/** What the server did not apply of a crew write, because ops had changed the run first (#86b). */
export type NavigatorSaveDropped = 'moved' | 'reset';

/**
 * The ops revision a write should be based on: the newest this device knows of, from the event as
 * loaded or from any reply since.
 *
 * `undefined` — no check — when the event was loaded without the field at all: a job kept on the
 * phone from before #86b. Counting that as revision 0 would drop every save once ops had ever moved
 * the run. A loaded event with the field but no value is revision 0.
 */
export function opsRevisionToSend(
  event: { navigatorOpsRevision?: number | null } | null | undefined,
  known: number | undefined,
): number | undefined {
  const loaded = event && 'navigatorOpsRevision' in event ? (event.navigatorOpsRevision ?? 0) : undefined;
  if (loaded === undefined) { return known; }
  return Math.max(loaded, known ?? 0);
}

/** A write waiting in the outbox for a connection. */
export interface QueuedNavigatorWrite {
  id: string;
  /**
   * When the write was built, in this device's order. Only ever compared with other writes from
   * the same device, so a phone clock that is off does not matter.
   */
  seq: number;
  jobId: string | null;
  write: NavigatorWrite;
}

/** Keys merged per step id on the server (#48); every other top-level key is replaced whole. */
const PER_STEP_KEYS = [ 'steps', 'stepLocations' ];

/** The step timing keys the server reads a cleared-step signal from. */
const TIMING_KEYS = [ 'startedAt', 'completedAt', 'start', 'end', 'duration' ];

/** The HTTP statuses a gateway answers with when the backend cannot be reached. */
const GATEWAY_STATUS_MIN = 502;
const GATEWAY_STATUS_MAX = 504;

let lastSeq = 0;

/** Strictly increasing, also across a restart (it starts from the clock). */
export function nextNavigatorWriteSeq(): number {
  lastSeq = Math.max(Date.now(), lastSeq + 1);
  return lastSeq;
}

function isPlainObject(val: unknown): val is Record<string, any> {
  return Boolean(val) && typeof val === 'object' && !Array.isArray(val);
}

function parseObject(json: string | null | undefined): Record<string, any> | null {
  try {
    const parsed = json ? JSON.parse(json) : null;
    return isPlainObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Whether a failed write failed for lack of signal — the only failure the outbox holds on to.
 *
 * A server refusal (a GraphQL error, or any HTTP answer other than a gateway's) is not retried:
 * resending it would be refused again, forever. It keeps today's behaviour instead — the step is
 * rolled back and the crew is told.
 */
export function isNoSignalError(error: any): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) { return true; }
  if (!error || error.graphQLErrors?.length) { return false; }

  const networkError = error.networkError;
  if (!networkError) { return false; }

  const status: number | undefined = networkError.statusCode;
  if (status === undefined || status === 0) { return true; }
  return status >= GATEWAY_STATUS_MIN && status <= GATEWAY_STATUS_MAX;
}

/**
 * What is left of an older queued write once a newer write from this device has been stored.
 *
 * Writes leave the device concurrently, so on a weak signal a later one can land while an earlier
 * one fails and waits here. Replaying the earlier one as it stands would then put back what the
 * later one replaced — the step position, and the step entry the later write completed. So the
 * older write loses every key the newer one carried, at the server's merge granularity (#48):
 * `currentStep`, each top-level key, and each step id under `steps` / `stepLocations`.
 *
 * Returns `null` when nothing is left to send.
 */
export function withoutKeysWrittenBy(older: NavigatorWrite, newer: NavigatorWrite): NavigatorWrite | null {
  const olderData = parseObject(older.navigatorData);
  const newerData = parseObject(newer.navigatorData);

  const currentStep = newer.currentStep === undefined ? older.currentStep : undefined;
  const inventoryChanges = inventoryChangesWithout(older.inventoryChanges, newer.inventoryChanges);
  const carried = {
    ...(older.basedOnOpsRevision === undefined ? {} : { basedOnOpsRevision: older.basedOnOpsRevision }),
    ...(inventoryChanges === undefined ? {} : { inventoryChanges }),
  };

  // Not objects: the server replaces the stored value outright, so the newer write wins whole.
  if (!olderData || !newerData) {
    return currentStep === undefined && inventoryChanges === undefined ? null : {
      eventId: older.eventId,
      ...(currentStep === undefined ? {} : { currentStep }),
      navigatorData: '{}',
      ...carried,
    };
  }

  const remaining: Record<string, any> = {};
  for (const [ key, value ] of Object.entries(olderData)) {
    if (PER_STEP_KEYS.includes(key) && isPlainObject(value) && isPlainObject(newerData[key])) {
      const entries = Object.entries(value).filter(([ stepId ]) => !(stepId in newerData[key]));
      if (entries.length) { remaining[key] = Object.fromEntries(entries); }
    } else if (!(key in newerData)) {
      remaining[key] = value;
    }
  }

  if (currentStep === undefined && inventoryChanges === undefined && Object.keys(remaining).length === 0) { return null; }

  return {
    eventId: older.eventId,
    ...(currentStep === undefined ? {} : { currentStep }),
    navigatorData: JSON.stringify(remaining),
    ...carried,
  };
}

/**
 * An older write's inventory changes, less every item and room a newer one also changed (#86c): the
 * newer one already set them from where the crew left them, so replaying the older one would put
 * back what it replaced. `undefined` when nothing is left.
 */
function inventoryChangesWithout(older: string | undefined, newer: string | undefined): string | undefined {
  if (older === undefined) { return undefined; }
  const olderChanges = parseObject(older) as InventoryChanges | null;
  const newerChanges = parseObject(newer) as InventoryChanges | null;
  if (!olderChanges || !newerChanges) { return older; }

  const newerItems = new Set((newerChanges.items ?? []).map((c) => c.id));
  const newerRooms = new Set((newerChanges.rooms ?? []).map((c) => c.id));
  const items = (olderChanges.items ?? []).filter((c) => !newerItems.has(c.id));
  const rooms = (olderChanges.rooms ?? []).filter((c) => !newerRooms.has(c.id));
  return items.length || rooms.length ? JSON.stringify({ rooms, items }) : undefined;
}

/** Derived from weight / volume × quantity whenever the list is saved; not a change of the crew's. */
const DERIVED_ITEM_KEYS = [ 'totalWeight', 'totalVolume' ];

/** A stable text of an item, key order and derived totals aside. */
function itemText(item: Record<string, any>): string {
  const keys = Object.keys(item).filter((k) => !DERIVED_ITEM_KEYS.includes(k) && item[k] !== undefined).sort();
  return JSON.stringify(keys.map((k) => [ k, item[k] ]));
}

interface InventoryRoomLike {
  id?: string;
  name: string;
  inventory?: Record<string, any>[];
}

/**
 * The crew's inventory changes (#86c): the list as the server last had it (`base`, from the job as
 * loaded) against the list on screen (`current`). Per item, what the crew started from and what
 * they set; added and removed items and rooms; renamed rooms. `null` when nothing changed.
 *
 * Inventories from before ids have none stored: the job page invents them each time it loads the
 * list, so the stored room or item without one is the one at the same position on screen — the
 * navigator only ever appends rooms and items, it never reorders or removes them.
 */
export function inventoryChangesBetween(
  base: InventoryRoomLike[],
  current: InventoryRoomLike[],
): InventoryChanges | null {
  const baseRooms = base.map((room, r) => {
    const ownId = room.id;
    const id = ownId ?? current[r]?.id;
    const items = (room.inventory ?? []).map((item, i) => (item.id
      ? item
      : { ...item, id: current[r]?.inventory?.[i]?.id }));
    return { ...room, id, ownId, inventory: items };
  });

  const baseItems = new Map<string, Record<string, any>>();
  for (const room of baseRooms) {
    for (const item of room.inventory) {
      if (item.id) { baseItems.set(item.id, item); }
    }
  }

  const changes: InventoryChanges = { rooms: [], items: [] };
  const seen = new Set<string>();

  for (const room of current) {
    if (!room.id) { continue; }
    const baseRoom = baseRooms.find((r) => r.id === room.id);
    if (!baseRoom) {
      const { inventory: _items, ...fields } = room;
      changes.rooms.push({ id: room.id, before: null, after: fields });
    } else if (baseRoom.name !== room.name) {
      changes.rooms.push({ id: room.id, before: { name: baseRoom.name }, after: { name: room.name } });
    }

    for (const item of room.inventory ?? []) {
      if (!item.id) { continue; }
      seen.add(item.id);
      const before = baseItems.get(item.id) ?? null;
      if (before && itemText(before) === itemText(item)) { continue; }
      changes.items.push({
        id: item.id,
        roomId: room.id,
        roomName: baseRoom?.name ?? room.name,
        before,
        after: item,
      });
    }
  }

  for (const room of baseRooms) {
    // A room the server has but the screen doesn't was deleted — record it
    // even when it held no items: an empty room's deletion used to leave
    // nothing in the delta, Confirm Scope omitted inventoryChanges, and a
    // reload restored the room (CodeRabbit). `after: null`, which the server
    // applies as a room removal. Rooms stored without an id (inventories
    // from before room ids) are skipped: the id here is borrowed from the
    // screen's same position, not the room's own.
    if (room.ownId && !current.some((r) => r.id === room.id)) {
      const { inventory: _items, ownId: _own, ...fields } = room;
      changes.rooms.push({ id: room.id, before: fields, after: null });
    }
    for (const item of room.inventory) {
      if (!item.id || seen.has(item.id)) { continue; }
      changes.items.push({ id: item.id, roomId: room.id ?? '', roomName: room.name, before: item, after: null });
    }
  }

  return changes.rooms.length || changes.items.length ? changes : null;
}

/**
 * The rooms with the crew's refused inventory changes taken out again: the list as the server
 * still has it, as far as this device knows.
 *
 * Only where the item still holds the crew's value — an update from the server since then already
 * shows what is stored, and is left alone. Items first, so a room the crew added is empty by the
 * time it is checked; a room still holding other items stays.
 */
export function withoutInventoryChanges<R extends InventoryRoomLike>(rooms: R[], changes: InventoryChanges): R[] {
  const result = rooms.map((room) => ({ ...room, inventory: [ ...(room.inventory ?? []) ] }));
  const locate = (id: string) => {
    for (const room of result) {
      const index = room.inventory.findIndex((item) => item.id === id);
      if (index >= 0) { return { room, index }; }
    }
    return null;
  };

  // Rooms the crew deleted come back first, empty (CodeRabbit): a deletion
  // entry's `before` holds the room but not its items — those ride as item
  // changes below and repopulate it. One already present had its deletion
  // applied server-side after all; nothing to undo.
  for (const change of changes.rooms) {
    if (!change.before || change.after !== null) { continue; }
    if (result.some((room) => room.id === change.id)) { continue; }
    result.push({ ...change.before, id: change.id, inventory: [] } as R & { inventory: Record<string, any>[] });
  }

  for (const change of changes.items) {
    const found = locate(change.id);
    if (change.after === null) {
      const room = result.find((r) => r.id === change.roomId) ?? result.find((r) => r.name === change.roomName);
      if (!found && room && change.before) { room.inventory.push(change.before); }
    } else if (found && itemText(found.room.inventory[found.index]) === itemText(change.after)) {
      if (change.before) {
        found.room.inventory.splice(found.index, 1, change.before);
      } else {
        found.room.inventory.splice(found.index, 1);
      }
    }
  }

  const undone: R[] = [];
  for (const room of result) {
    const change = changes.rooms.find((c) => c.id === room.id);
    if (change && !change.before && !room.inventory.length) { continue; }
    if (change?.before && change.after && room.name === change.after.name) {
      undone.push({ ...room, name: change.before.name });
      continue;
    }
    undone.push(room);
  }
  return undone;
}

function isClearedStep(timing: any): boolean {
  return isPlainObject(timing)
    && TIMING_KEYS.some((k) => k in timing)
    && TIMING_KEYS.every((k) => !(k in timing) || timing[k] == null);
}

/**
 * The run as the server will store it once a queued write lands — the client's copy of the
 * server's merge (#48) and of its cleared-step signal, which deletes the entry.
 *
 * Used to keep this device's copy of the run up to date while its writes wait, so the timer and a
 * return to the navigator read the crew's own progress rather than the last copy the server sent.
 */
export function mergeQueuedNavigatorData(stored: string | null | undefined, incoming: string): string {
  const patch = parseObject(incoming);
  if (!patch) { return incoming; }

  const current = parseObject(stored) ?? {};
  const merged: Record<string, any> = { ...current, ...patch };

  for (const key of PER_STEP_KEYS) {
    if (isPlainObject(current[key]) && isPlainObject(patch[key])) {
      merged[key] = { ...current[key], ...patch[key] };
    }
  }

  if (isPlainObject(merged.steps)) {
    merged.steps = Object.fromEntries(
      Object.entries(merged.steps).filter(([ , timing ]) => !isClearedStep(timing)),
    );
  }

  return JSON.stringify(merged);
}

/**
 * Whether a queued write is a save the crew made, for the "N saves waiting" count. A position
 * stamp (`stepLocations` only) rides along after each step but is not one the crew would recognise.
 */
export function isCrewSave(entry: QueuedNavigatorWrite): boolean {
  if (entry.write.currentStep !== undefined) { return true; }
  const keys = Object.keys(parseObject(entry.write.navigatorData) ?? {});
  return !(keys.length === 1 && keys[0] === 'stepLocations');
}
